// 🎙️ VoiceOrb — orbe flotante de dictado por voz (asistente, Fase 1).
//
// Visible solo si la empresa tiene modo de voz ≠ 'desactivado' Y el perfil del
// usuario tiene la acción 'voz_dictado' habilitada (Sistemas → Asistente).
// Escucha por el voice-gateway (STT local, el audio no sale del servidor) y
// cada segmento FINAL se inserta en el campo de texto que tenga el foco.
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, Loader2, Mic, Sparkles, Square, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, esModuloAusente } from '@/lib/api/client';
import { useAuth } from '@/lib/hooks';
import { useVoiceSession, type ItemRxDetectado } from '@/lib/hooks/useVoiceSession';
import { useVoiceFormActivo, type ItemRecetaVoz } from '@/contexts/VoiceFormContext';
import { cn } from '@/lib/utils';

interface SugerenciasPend {
  salidaId: string;
  pendientes: { clave: string; etiqueta: string; valor: unknown }[];
  notas: string[];
}

interface RxPend extends ItemRxDetectado {
  key: string;          // identidad para dedup entre pasadas de estructuración
  elegido: string;      // producto_id elegido ('' = solo texto, sin catálogo)
}

const rxKey = (i: ItemRxDetectado) =>
  `${i.farmaco.toLowerCase()}|${(i.concentracion ?? '').toLowerCase()}|${i.cantidad ?? ''}`;

// Preseleccionar SOLO si el match es inequívoco (candidato razonable Y con clara
// ventaja). OJO: las similitudes reales rondan 0.3-0.4 porque el nombre del
// catálogo incluye la concentración ("Losartán 1 g" vs "losartan" dictado);
// las variantes de presentación empatan en similitud → sin ventaja → el médico elige.
const preseleccion = (c: ItemRxDetectado['candidatos']): string => {
  if (!c.length || c[0].similitud < 0.35) return '';
  if (c.length > 1 && c[0].similitud - c[1].similitud < 0.15) return '';
  return c[0].id;
};

const ASIST = { headers: { 'Content-Profile': 'asistente', 'Accept-Profile': 'asistente' } };

interface VozEstado { modo: 'desactivado' | 'dictado' | 'estructurado' | 'completo'; dictado: boolean }

/** Inserta texto en el punto del cursor de un input/textarea usando el setter
 *  nativo + evento 'input' (para que React vea el cambio como si lo tecleara). */
function insertarEnCampo(el: HTMLElement | null, texto: string): boolean {
  if (!el || !el.isConnected) return false;
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    if (el.disabled || el.readOnly) return false;
    const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
    if (!setter) return false;
    let start = el.value.length;
    let end = start;
    try { start = el.selectionStart ?? start; end = el.selectionEnd ?? start; } catch { /* inputs sin selección */ }
    const sep = start > 0 && !/\s$/.test(el.value.slice(0, start)) ? ' ' : '';
    setter.call(el, el.value.slice(0, start) + sep + texto + el.value.slice(end));
    el.dispatchEvent(new Event('input', { bubbles: true }));
    const pos = start + sep.length + texto.length;
    try { el.setSelectionRange(pos, pos); } catch { /* number/date no soportan */ }
    return true;
  }
  if (el.isContentEditable) {
    el.focus();
    document.execCommand('insertText', false, `${texto} `);
    return true;
  }
  return false;
}

/** Ecualizador minimalista: 5 barras con vaivén propio, cuya amplitud global
 *  la manda el volumen real del micrófono (nivel). En silencio respiran bajito. */
function BarrasVoz({ nivel }: { nivel: number }) {
  const amp = 0.12 + Math.min(0.88, nivel * 9);
  const barras = [
    { dur: '0.9s', delay: '-0.2s' },
    { dur: '0.65s', delay: '-0.45s' },
    { dur: '1.05s', delay: '0s' },
    { dur: '0.7s', delay: '-0.3s' },
    { dur: '0.95s', delay: '-0.55s' },
  ];
  return (
    <span
      className="flex items-center justify-center gap-[3px] h-6"
      style={{ transform: `scaleY(${amp})`, transition: 'transform 90ms linear' }}
      aria-hidden
    >
      {barras.map((b, i) => (
        <span
          key={i}
          className="voz-barra w-[3px] h-full rounded-full bg-white"
          style={{ animationDuration: b.dur, animationDelay: b.delay }}
        />
      ))}
    </span>
  );
}

const ESTILO_BARRAS = `
@keyframes voz-eq { 0%, 100% { transform: scaleY(0.3); } 50% { transform: scaleY(1); } }
.voz-barra { animation: voz-eq 1s ease-in-out infinite; transform-origin: center; }
`;

const esDictable = (el: EventTarget | null): el is HTMLElement =>
  (el instanceof HTMLTextAreaElement) ||
  (el instanceof HTMLInputElement && !['password', 'checkbox', 'radio', 'file', 'button', 'submit'].includes(el.type)) ||
  (el instanceof HTMLElement && el.isContentEditable);

export function VoiceOrb() {
  const { isAuthenticated } = useAuth();
  const campoRef = useRef<HTMLElement | null>(null);
  const [historial, setHistorial] = useState<string[]>([]);
  const avisadoRef = useRef(false);

  const { data: voz } = useQuery<VozEstado>({
    queryKey: ['voz-estado'],
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
    retry: (n, e) => !esModuloAusente(e) && n < 3,   // sin módulo de asistente (TrackConstruccion): una sola consulta
    queryFn: async () => (await apiClient.post('/rpc/asistente_voz_estado', {}, ASIST)).data,
  });

  // Recordar el último campo de texto enfocado (el orbe roba el foco al hacer click)
  useEffect(() => {
    const onFocus = (e: FocusEvent) => { if (esDictable(e.target)) campoRef.current = e.target; };
    document.addEventListener('focusin', onFocus);
    return () => document.removeEventListener('focusin', onFocus);
  }, []);

  // ---- Fase 2: formulario activo registrado + sugerencias estructuradas ----
  const registro = useVoiceFormActivo();
  const [sugerencias, setSugerencias] = useState<SugerenciasPend | null>(null);
  const [sugRx, setSugRx] = useState<{ salidaId: string; items: RxPend[] } | null>(null);
  const [pensandoIA, setPensandoIA] = useState(false);
  const decididasRef = useRef(new Map<string, unknown>());   // clave → valor ya aplicado
  const descartadasRef = useRef(new Set<string>());          // claves rechazadas en esta sesión
  const rxDecididasRef = useRef(new Set<string>());          // prescripciones ya agregadas/descartadas
  const registroRef = useRef(registro);
  registroRef.current = registro;

  const conFormulario = !!registro && registro.campos.length > 0 && voz != null && voz.modo !== 'dictado';

  const sesion = useVoiceSession(
    (texto) => {
      setHistorial((h) => [...h.slice(-4), texto]);
      // en modo estructurado el destino principal es el formulario; la inserción
      // al campo activo sigue funcionando como complemento
      if (!insertarEnCampo(campoRef.current, texto) && !avisadoRef.current && !registroRef.current) {
        avisadoRef.current = true;
        toast.info('Haz click en un campo de texto para que el dictado se inserte ahí');
      }
    },
    (m) => {
      if (m.type === 'aviso') { toast.info(m.mensaje); return; }
      if (m.type === 'estructurando') { setPensandoIA(true); return; }
      if (m.type === 'form_fill' && m.salida_id && m.campos) {
        setPensandoIA(false);
        const reg = registroRef.current;
        const pendientes = Object.entries(m.campos)
          .filter(([k, v]) => !descartadasRef.current.has(k) && decididasRef.current.get(k) !== v)
          .map(([k, v]) => ({
            clave: k,
            etiqueta: reg?.campos.find((c) => c.clave === k)?.etiqueta ?? k,
            valor: v,
          }));
        if (pendientes.length) setSugerencias({ salidaId: m.salida_id, pendientes, notas: m.notas ?? [] });
      }
      if (m.type === 'receta_fill' && m.salida_id && m.items) {
        setPensandoIA(false);
        const items = m.items
          .filter((i) => !rxDecididasRef.current.has(rxKey(i)))
          .map((i) => ({ ...i, key: rxKey(i), elegido: preseleccion(i.candidatos) }));
        if (items.length) setSugRx({ salidaId: m.salida_id, items });
      }
    },
  );

  useEffect(() => {
    if (sesion.error) toast.error(sesion.error);
  }, [sesion.error]);

  if (!voz || voz.modo === 'desactivado' || !voz.dictado) return null;

  const activo = sesion.estado === 'escuchando' || sesion.estado === 'finalizando';
  const onda = Math.min(1, sesion.nivel * 8);

  const decidir = (salidaId: string, aceptado: boolean, detalle?: Record<string, unknown>) => {
    void apiClient.post('/rpc/asistente_voz_salida_decidir',
      { _salida: salidaId, _aceptado: aceptado, _detalle: detalle ?? null }, ASIST).catch(() => undefined);
  };

  const aplicarCampos = (items: { clave: string; valor: unknown }[]) => {
    registroRef.current?.aplicar(Object.fromEntries(items.map((i) => [i.clave, i.valor])));
    items.forEach((i) => decididasRef.current.set(i.clave, i.valor));
  };

  const aceptarTodo = () => {
    if (!sugerencias) return;
    aplicarCampos(sugerencias.pendientes);
    decidir(sugerencias.salidaId, true, { claves: sugerencias.pendientes.map((p) => p.clave) });
    setSugerencias(null);
    toast.success(`${sugerencias.pendientes.length} campo(s) aplicados al formulario`);
  };

  const descartarTodo = () => {
    if (!sugerencias) return;
    sugerencias.pendientes.forEach((p) => descartadasRef.current.add(p.clave));
    decidir(sugerencias.salidaId, false);
    setSugerencias(null);
  };

  const decidirUno = (clave: string, aceptar: boolean) => {
    if (!sugerencias) return;
    const item = sugerencias.pendientes.find((p) => p.clave === clave);
    if (!item) return;
    if (aceptar) aplicarCampos([item]);
    else descartadasRef.current.add(clave);
    const resto = sugerencias.pendientes.filter((p) => p.clave !== clave);
    if (!resto.length) {
      decidir(sugerencias.salidaId, aceptar || decididasRef.current.size > 0, {
        aceptadas: [...decididasRef.current.keys()], descartadas: [...descartadasRef.current],
      });
      setSugerencias(null);
    } else {
      setSugerencias({ ...sugerencias, pendientes: resto });
    }
  };

  // ---- prescripciones dictadas → filas de la receta ----
  const rxAItem = (p: RxPend): ItemRecetaVoz => {
    const cand = p.candidatos.find((c) => c.id === p.elegido);
    return {
      producto_id: cand?.id ?? null,
      medicamento: cand?.nombre ?? p.farmaco,
      comercial: p.comercial ?? '',
      concentracion: p.concentracion || cand?.concentracion || '',
      via: p.via ?? '',
      forma: p.forma || cand?.presentacion || '',
      cantidad: p.cantidad ?? '',
      indicaciones: p.indicaciones ?? '',
    };
  };

  const agregarRx = (key: string | null) => {   // null = todos
    if (!sugRx) return;
    const sel = key ? sugRx.items.filter((i) => i.key === key) : sugRx.items;
    if (!sel.length) return;
    registroRef.current?.aplicarReceta?.(sel.map(rxAItem));
    sel.forEach((i) => rxDecididasRef.current.add(i.key));
    decidir(sugRx.salidaId, true, { receta_agregados: sel.map((i) => ({ farmaco: i.farmaco, producto_id: i.elegido || null })) });
    const resto = sugRx.items.filter((i) => !sel.some((s) => s.key === i.key));
    setSugRx(resto.length ? { ...sugRx, items: resto } : null);
    toast.success(`${sel.length} medicamento(s) agregados a la receta`);
  };

  const descartarRx = (key: string | null) => {
    if (!sugRx) return;
    const sel = key ? sugRx.items.filter((i) => i.key === key) : sugRx.items;
    sel.forEach((i) => rxDecididasRef.current.add(i.key));
    if (!key) decidir(sugRx.salidaId, false, { receta_descartados: sel.map((i) => i.farmaco) });
    const resto = sugRx.items.filter((i) => !sel.some((s) => s.key === i.key));
    setSugRx(resto.length ? { ...sugRx, items: resto } : null);
  };

  const elegirCandidato = (key: string, id: string) =>
    setSugRx((s) => (s ? { ...s, items: s.items.map((i) => (i.key === key ? { ...i, elegido: id } : i)) } : s));

  const alternar = () => {
    if (activo) { sesion.detener(); return; }
    setHistorial([]);
    setSugerencias(null);
    setSugRx(null);
    setPensandoIA(false);
    decididasRef.current = new Map();
    descartadasRef.current = new Set();
    rxDecididasRef.current = new Set();
    avisadoRef.current = false;
    void sesion.iniciar(
      registro?.modulo ?? 'general',
      conFormulario && registro
        ? {
            paciente_id: registro.pacienteId ?? null,
            schema: { campos: registro.campos, ...(registro.receta ? { receta: registro.receta } : {}) },
          }
        : undefined,
    );
  };

  const copiarTodo = async () => {
    await navigator.clipboard.writeText(historial.join(' '));
    toast.success('Dictado copiado');
  };

  return (
    <div className="fixed left-6 z-40 flex flex-col items-start gap-2" style={{ bottom: 'calc(1.5rem + var(--alto-barra-app, 0px))' }}>
      {/* Panel de transcripción + sugerencias */}
      {(activo || sesion.estado === 'conectando' || sugerencias || sugRx || pensandoIA) && (
        <div className="w-80 max-w-[80vw] rounded-2xl border border-border bg-background shadow-2xl p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-muted-foreground">
              {sesion.estado === 'conectando' && 'Conectando…'}
              {sesion.estado === 'escuchando' && (conFormulario
                ? <>Escuchando — dictando para <span className="text-foreground">{registro?.modulo}</span> ✨</>
                : 'Escuchando — el texto se inserta en el campo activo')}
              {sesion.estado === 'finalizando' && 'Procesando lo último…'}
              {sesion.estado === 'idle' && (sugerencias || sugRx || pensandoIA) && 'Dictado terminado'}
            </span>
            {historial.length > 0 && (
              <button onClick={copiarTodo} className="text-muted-foreground hover:text-foreground" title="Copiar todo el dictado">
                <Copy className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {historial.length > 0 && (
            <div className="max-h-28 overflow-y-auto text-sm space-y-1">
              {historial.map((t, i) => <p key={i}>{t}</p>)}
            </div>
          )}
          {sesion.parcial && <p className="text-sm italic text-muted-foreground">{sesion.parcial}…</p>}
          {!sesion.parcial && historial.length === 0 && sesion.estado === 'escuchando' && !sesion.sinSenal && (
            <p className="text-sm text-muted-foreground">Habla con normalidad; al pausar, la frase se confirma.</p>
          )}
          {/* Sugerencias del asistente (ámbar hasta que el médico decida) */}
          {pensandoIA && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Sparkles className="h-3 w-3 animate-pulse text-amber-500" /> Estructurando el dictado…
            </p>
          )}
          {sugerencias && (
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-2 space-y-1.5">
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> Campos detectados — revisa y acepta
              </p>
              <div className="max-h-44 overflow-y-auto space-y-1">
                {sugerencias.pendientes.map((p) => (
                  <div key={p.clave} className="flex items-start gap-1.5 text-xs">
                    <div className="flex-1 min-w-0">
                      <span className="text-muted-foreground">{p.etiqueta}:</span>{' '}
                      <span className="font-medium break-words">{p.valor === true ? 'Sí' : p.valor === false ? 'No' : String(p.valor)}</span>
                    </div>
                    <button onClick={() => decidirUno(p.clave, true)} title="Aplicar este campo"
                      className="shrink-0 rounded p-0.5 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40">
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => decidirUno(p.clave, false)} title="Descartar"
                      className="shrink-0 rounded p-0.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              {sugerencias.notas.length > 0 && (
                <p className="text-[10px] text-amber-700 dark:text-amber-400">⚠ {sugerencias.notas.join(' · ')}</p>
              )}
              <div className="flex gap-2 pt-0.5">
                <button onClick={aceptarTodo}
                  className="flex-1 rounded-md bg-green-600 hover:bg-green-700 text-white text-xs font-medium py-1">
                  Aceptar todo
                </button>
                <button onClick={descartarTodo}
                  className="rounded-md border border-border text-xs px-2 py-1 text-muted-foreground hover:bg-muted">
                  Descartar
                </button>
              </div>
            </div>
          )}

          {/* Prescripciones detectadas: el médico elige el producto del catálogo (o solo texto) */}
          {sugRx && (
            <div className="rounded-lg border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 p-2 space-y-1.5">
              <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-300 flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> Medicamentos detectados — elige el producto y agrega
              </p>
              <div className="max-h-52 overflow-y-auto space-y-2">
                {sugRx.items.map((p) => (
                  <div key={p.key} className="space-y-1 border-b border-emerald-200/60 dark:border-emerald-800/60 last:border-0 pb-1.5 last:pb-0">
                    <div className="flex items-start gap-1.5 text-xs">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">{p.farmaco}</span>
                        {p.concentracion && <span className="text-muted-foreground"> {p.concentracion}</span>}
                        {(p.cantidad || p.indicaciones) && (
                          <div className="text-[11px] text-muted-foreground truncate" title={p.indicaciones}>
                            {p.cantidad && <>×{p.cantidad} · </>}{p.indicaciones}
                          </div>
                        )}
                      </div>
                      <button onClick={() => agregarRx(p.key)} title="Agregar a la receta"
                        className="shrink-0 rounded p-0.5 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40">
                        <Check className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => descartarRx(p.key)} title="Descartar"
                        className="shrink-0 rounded p-0.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <select
                      className="w-full bg-background border border-border rounded px-1 py-0.5 text-[11px]"
                      value={p.elegido}
                      onChange={(e) => elegirCandidato(p.key, e.target.value)}
                      title="Producto del catálogo (la receta descuenta de inventario solo con producto elegido)"
                    >
                      <option value="">✎ Solo texto (sin producto del catálogo)</option>
                      {p.candidatos.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nombre}{c.concentracion ? ` ${c.concentracion}` : ''}{c.presentacion ? ` · ${c.presentacion}` : ''} ({Math.round(c.similitud * 100)}%)
                        </option>
                      ))}
                    </select>
                    {!p.candidatos.length && (
                      <p className="text-[10px] text-muted-foreground">Sin coincidencias en el catálogo — se agrega como texto.</p>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-0.5">
                <button onClick={() => agregarRx(null)}
                  className="flex-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium py-1">
                  Agregar todos a la receta
                </button>
                <button onClick={() => descartarRx(null)}
                  className="rounded-md border border-border text-xs px-2 py-1 text-muted-foreground hover:bg-muted">
                  Descartar
                </button>
              </div>
            </div>
          )}

          {sesion.sinSenal && sesion.estado === 'escuchando' && (
            <p className="text-xs rounded-md bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 p-2">
              ⚠️ «{sesion.microfono}» está entregando silencio. Revisa el mute del sistema o
              elige otro micrófono aquí abajo.
            </p>
          )}
          {sesion.estado === 'escuchando' && (
            sesion.mics.length > 1 ? (
              <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
                🎙
                <select
                  className="flex-1 min-w-0 bg-transparent border border-border rounded px-1 py-0.5 text-[11px] truncate"
                  value={sesion.mics.some((m) => m.id === sesion.micId) ? sesion.micId : ''}
                  onChange={(e) => {
                    sesion.cambiarMic(e.target.value);
                    toast.info('Micrófono cambiado — vuelve a pulsar el orbe para dictar');
                  }}
                  title="Elegir micrófono (se recuerda para la próxima vez)"
                >
                  <option value="">(predeterminado del navegador)</option>
                  {sesion.mics.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </label>
            ) : (
              <p className="text-[10px] text-muted-foreground truncate" title={sesion.microfono}>
                🎙 {sesion.microfono}
              </p>
            )
          )}
        </div>
      )}

      {/* Orbe */}
      <style>{ESTILO_BARRAS}</style>
      <button
        onClick={alternar}
        aria-label={activo ? 'Detener dictado' : 'Iniciar dictado por voz'}
        title={activo ? 'Detener dictado' : 'Dictado por voz'}
        className={cn(
          'group relative w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-colors',
          activo ? 'bg-red-600 hover:bg-red-700' : 'bg-gradient-to-br from-teal-500 to-blue-600 hover:opacity-90',
        )}
      >
        {/* anillo reactivo al volumen */}
        {sesion.estado === 'escuchando' && (
          <span
            className="absolute inset-0 rounded-full border-2 border-red-400/70"
            style={{ transform: `scale(${1 + onda * 0.45})`, opacity: 0.9 - onda * 0.5, transition: 'transform 120ms linear, opacity 120ms linear' }}
          />
        )}
        {sesion.estado === 'conectando' || sesion.estado === 'finalizando' ? (
          <Loader2 className="h-6 w-6 text-white animate-spin" />
        ) : sesion.estado === 'escuchando' ? (
          <>
            {/* barras que bailan con la voz; al pasar el mouse, el stop */}
            <span className="group-hover:opacity-0 transition-opacity">
              <BarrasVoz nivel={sesion.nivel} />
            </span>
            <Square className="absolute h-5 w-5 text-white fill-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </>
        ) : (
          <Mic className="h-6 w-6 text-white" />
        )}
      </button>
    </div>
  );
}
